import React, { useState } from "react";
import Image from "../../../../common/image";
import {
  currency,
  employmentType,
  roleNames,
} from "../../../../../constants/constants";
import { IAssignment, IEmployee, IPayslip, IPolicy } from ".";
import { formatDate } from "../../../../../utils/date-format";
import DetailRow from "../../../../common/detail-row";
import StatusUpdate from "./update-modals/StatusUpdate";
import StatusBadge from "../../../../common/badge/StatusBadge";
import DesignationUpdate from "./update-modals/DesignationUpdate";
import EmploymentTypeUpdate from "./update-modals/EmploymentTypeUpdate";
import ProbationPeriodUpdate from "./update-modals/ProbationPeriodUpdate";
import BranchAssignmentUpdate from "./update-modals/BranchAssignmentUpdate";
import { assignRolesAndResponsibility } from "../../../../../apis/workforce/onboardings.api";
import { HistoryFieldEnum, RoleEnum } from "../../../../../types/common-types";
import PolicyUpdate from "./update-modals/PolicyUpdate";
import SalaryUpdate from "./update-modals/SalaryUpdate";
import BranchDepartmentCards from "./BranchDepartments";
import { IEmployeeDetails } from "../../onboarding/employee-details";
import HistoryModal from "../../../../common/modal/HistoryModal";
import {
  HistoryPayload,
  initialHistory,
} from "../../../../../apis/history/history.api";
import InfoIcon from "../../../../../assets/icons/Info";
import AssignmentHistoryModal from "../../../../common/modal/AssignmentHistoryModal";
import { getBranches } from "../../../../../utils/helper";
import { useAuthStore } from "../../../../../store/auth-store";
import SalaryHistoryModal from "../../../../common/modal/SalaryHistoryModal";

interface Props {
  employeeData: IEmployee;
  employeeDetails: IEmployeeDetails;
  assignments: IAssignment[];
  policy: IPolicy;
  payslip: IPayslip;
  refreshData: () => void;
}

export enum EmployeeUpdateModal {
  STATUS = "status",
  DESIGNATION = "designation",
  EMPLOYMENT_TYPE = "employmentType",
  PROBATION_PERIOD = "probationPeriod",
  REPORTING_BRANCH = "EMPLOYEE",
  MANAGE_BRANCH = "MANAGER",
  POLICY = "Policy",
  SALARY = "Salary",
}

const EmployeeDetailCard: React.FC<Props> = ({
  employeeData,
  employeeDetails,
  assignments,
  policy,
  payslip,
  refreshData,
}) => {
  const { user } = useAuthStore();
  const isManager = employeeData.role === RoleEnum.MANAGER;
  const isOwner = user.role === RoleEnum.OWNER;

  const isEditable =
    user._id === employeeData._id && employeeData.role === RoleEnum.MANAGER;

  const designation = employeeData.designationId
    ? employeeData.designationId
    : { name: "", _id: "" };
  const manageBranches = assignments.filter((ele) => !ele.isReporting);
  const reportingBranch = isManager
    ? assignments.find((ele) => ele.isReporting)
    : assignments[0];

  const [update, setUpdate] = useState<EmployeeUpdateModal | "">("");
  const [loading, setLoading] = useState<boolean>(false);

  // history states
  const [historyOpen, setHistoryOpen] = useState<boolean>(false);
  const [history, setHistory] = useState<HistoryPayload>(initialHistory);

  // assignment history states
  const [assignmentHistoryOpen, setAssignmentHistoryOpen] =
    useState<boolean>(false);
  const [assignmentHistory, setAssignmentHistory] =
    useState<HistoryPayload>(initialHistory);

  // salary history states
  const [salaryHistoryOpen, setSalaryHistoryOpen] = useState<boolean>(false);
  const [salaryHistory, setSalaryHistory] =
    useState<HistoryPayload>(initialHistory);

  const handleSubmit = async (payload: any) => {
    setLoading(true);
    const response = await assignRolesAndResponsibility({
      userId: employeeData._id,
      ...payload,
    });
    if (response.success) {
      setUpdate("");
      refreshData();
    }
    setLoading(false);
  };

  // handle history open
  const handleHistoryOpenClose = () => {
    setHistoryOpen((prev) => !prev);
    setHistory(initialHistory);
  };

  // handle assignment history open
  const handleAssignmentHistoryOpenClose = () => {
    setAssignmentHistoryOpen((prev) => !prev);
    setAssignmentHistory(initialHistory);
  };

  // handle Salary history open
  const handleSalaryHistoryOpenClose = () => {
    setSalaryHistoryOpen((prev) => !prev);
    setSalaryHistory(initialHistory);
  };

  // handle show history
  const handleShowHistory = (employee: IEmployee, field: HistoryFieldEnum) => {
    if (field === HistoryFieldEnum.Assignment) {
      handleAssignmentHistoryOpenClose();
      setAssignmentHistory({
        field,
        fieldId: employee._id,
        title: `${employee.firstName} ${employee.lastName}`,
      });
    } else if (field === HistoryFieldEnum.SalaryStatus) {
      handleSalaryHistoryOpenClose();
      setSalaryHistory({
        field,
        fieldId: employee._id,
        title: `${employee.firstName} ${employee.lastName}`,
      });
    } else {
      handleHistoryOpenClose();
      setHistory({
        field,
        fieldId: employee._id,
        title: `${employee.firstName} ${employee.lastName}`,
      });
    }
  };

  const cards = manageBranches?.length > 0 ? getBranches(manageBranches) : [];
  const reportingCards = reportingBranch ? getBranches([reportingBranch]) : [];
  return (
    <>
      <div className="content-card bg-white border border-gray-200">
        {/* Header */}
        <div className="bg-primary p-2.5 flex items-center gap-4">
          <Image
            src={employeeData.profileImage}
            alt={employeeData.firstName}
            className="w-20
            h-20
            min-w-20
            min-h-20
            shrink-0
            object-cover
            rounded-full"
          />
          <div>
            <h2 className="text-lg text-white font-medium">
              {employeeData.firstName} {employeeData.lastName}
            </h2>
            <span className="text-sm text-white">{employeeData.userId}</span>
          </div>
        </div>

        <div className="p-3 sm:p-5">
          <div className="flex items-center gap-2 border-b pb-3 mb-4">
            <i className="fa-solid fa-user-pen text-secondary"></i>
            <h3 className="text-md text-gray-600 font-medium">
              Employee Configuration
            </h3>
          </div>
          <div className="space-y-4">
            <DetailRow
              label="Employee Full Name"
              value={
                <>
                  {employeeData.firstName} {employeeData.lastName}
                </>
              }
            />
            <DetailRow
              label="Created Date"
              value={formatDate(employeeData.createdAt)}
            />

            <DetailRow
              label="Status"
              value={
                <div className="flex items-center gap-2 mr-1">
                  <StatusBadge status={employeeData.status} />
                  <InfoIcon
                    onClick={() =>
                      handleShowHistory(
                        employeeData,
                        HistoryFieldEnum.UserStatus,
                      )
                    }
                  />
                  {!isEditable && (
                    <i
                      className="fa-solid fa-pen-to-square cursor-pointer text-lg sm:text-sm text-gray-400 hover:text-secondary"
                      onClick={() => setUpdate(EmployeeUpdateModal.STATUS)}
                    ></i>
                  )}
                </div>
              }
            />
          </div>
        </div>
        <div className="p-3 sm:p-5">
          <div className="flex items-center gap-2 border-b pb-3 mb-4">
            <i className="fa-solid fa-user-gear"></i>
            <h3 className="text-md text-gray-600 font-medium">
              Employment Details
            </h3>
          </div>

          <div className="space-y-4">
            <DetailRow
              label="Organizational Role"
              value={
                <div className="flex items-center gap-2 mr-1">
                  <span>{roleNames[employeeData.role]}</span>
                  <InfoIcon
                    onClick={() =>
                      handleShowHistory(employeeData, HistoryFieldEnum.Role)
                    }
                  />
                </div>
              }
            />

            <DetailRow
              label="Designation"
              value={
                <div className="flex items-center gap-2 mr-1">
                  <span>{designation.name}</span>
                  <InfoIcon
                    onClick={() =>
                      handleShowHistory(
                        employeeData,
                        HistoryFieldEnum.Designation,
                      )
                    }
                  />
                  {isOwner && (
                    <i
                      onClick={() => setUpdate(EmployeeUpdateModal.DESIGNATION)}
                      className="fa-solid fa-pen-to-square cursor-pointer text-lg sm:text-sm text-gray-400 hover:text-secondary"
                    ></i>
                  )}
                </div>
              }
            />

            {employeeData.role === RoleEnum.MANAGER && (
              <div className="flex justify-between text-sm border-b border-gray-200 pb-3">
                <div className="text-gray-700">
                  Managed Branch & Departments
                </div>
                <div className="flex items-center gap-2 mr-1">
                  <BranchDepartmentCards cards={cards} />
                  <InfoIcon
                    onClick={() =>
                      handleShowHistory(
                        employeeData,
                        HistoryFieldEnum.Assignment,
                      )
                    }
                  />
                  {isOwner && (
                    <i
                      onClick={() =>
                        setUpdate(EmployeeUpdateModal.MANAGE_BRANCH)
                      }
                      className="fa-solid fa-pen-to-square cursor-pointer text-lg sm:text-sm text-gray-400 hover:text-secondary"
                    ></i>
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-between text-sm border-b border-gray-200 pb-3">
              <div className="text-gray-700">Reporting Branch & Shift</div>
              <div className="flex items-center gap-2 mr-1">
                <BranchDepartmentCards cards={reportingCards} />
                <InfoIcon
                  onClick={() =>
                    handleShowHistory(employeeData, HistoryFieldEnum.Assignment)
                  }
                />
                {isOwner && (
                  <i
                    onClick={() =>
                      setUpdate(EmployeeUpdateModal.REPORTING_BRANCH)
                    }
                    className="fa-solid fa-pen-to-square cursor-pointer text-lg sm:text-sm text-gray-400 hover:text-secondary"
                  ></i>
                )}
              </div>
            </div>

            <DetailRow
              label="Employment Type"
              value={
                <div className="flex items-center gap-2 mr-1">
                  <span>{employmentType[employeeData.employmentType]}</span>
                  <InfoIcon
                    onClick={() =>
                      handleShowHistory(
                        employeeData,
                        HistoryFieldEnum.EmploymentType,
                      )
                    }
                  />
                  {!isEditable && (
                    <i
                      onClick={() =>
                        setUpdate(EmployeeUpdateModal.EMPLOYMENT_TYPE)
                      }
                      className="fa-solid fa-pen-to-square cursor-pointer text-lg sm:text-sm text-gray-400 hover:text-secondary"
                    ></i>
                  )}
                </div>
              }
            />

            <DetailRow
              label="Probational Period"
              value={
                <div className="flex items-center gap-2 mr-1">
                  <span>
                    {employeeData.probationPeriod}
                    {` Month${employeeData.probationPeriod > 1 ? "s" : ""}`}
                  </span>
                  <InfoIcon
                    onClick={() =>
                      handleShowHistory(
                        employeeData,
                        HistoryFieldEnum.ProbationPeriod,
                      )
                    }
                  />
                  {!isEditable && (
                    <i
                      onClick={() =>
                        setUpdate(EmployeeUpdateModal.PROBATION_PERIOD)
                      }
                      className="fa-solid fa-pen-to-square cursor-pointer text-lg sm:text-sm text-gray-400 hover:text-secondary"
                    ></i>
                  )}
                </div>
              }
            />

            <DetailRow
              label="Policy Applied"
              value={
                <div className="flex items-center gap-2 mr-1">
                  <span>{policy?.policyId?.name}</span>
                  {/* <InfoIcon
                    onClick={() =>
                      handleShowHistory(
                        employeeData,
                        HistoryFieldEnum.EmploymentType,
                      )
                    }
                  /> */}
                  {/* {!isEditable && (
                    <i
                      onClick={() => setUpdate(EmployeeUpdateModal.POLICY)}
                      className="fa-solid fa-pen-to-square cursor-pointer text-gray-400 hover:text-secondary"
                    ></i>
                  )} */}
                </div>
              }
            />

            <DetailRow
              label="Salary Details"
              value={
                <div className="flex items-center gap-2 mr-1">
                  <span>
                    {currency.INR} {payslip.salary} {" / Month"}
                  </span>
                  <InfoIcon
                    onClick={() =>
                      handleShowHistory(
                        employeeData,
                        HistoryFieldEnum.SalaryStatus,
                      )
                    }
                  />
                  {!isEditable && (
                    <i
                      onClick={() => setUpdate(EmployeeUpdateModal.SALARY)}
                      className="fa-solid fa-pen-to-square cursor-pointer text-lg sm:text-sm text-gray-400 hover:text-secondary"
                    ></i>
                  )}
                </div>
              }
            />
          </div>
        </div>
      </div>
      <StatusUpdate
        active={update === EmployeeUpdateModal.STATUS}
        employeeData={employeeData}
        setActive={() => setUpdate("")}
        status={employeeData?.status}
        refreshData={refreshData}
      />
      <DesignationUpdate
        active={update === EmployeeUpdateModal.DESIGNATION}
        employeeData={employeeData}
        setActive={() => setUpdate("")}
        designationId={designation?._id}
        handleSubmit={handleSubmit}
        loading={loading}
      />
      <EmploymentTypeUpdate
        active={update === EmployeeUpdateModal.EMPLOYMENT_TYPE}
        employeeName={`${employeeData?.firstName} ${employeeData?.lastName}`}
        profileImage={employeeData?.profileImage}
        setActive={() => setUpdate("")}
        employmentType={employeeData?.employmentType}
        handleSubmit={handleSubmit}
        loading={loading}
      />
      <ProbationPeriodUpdate
        active={update === EmployeeUpdateModal.PROBATION_PERIOD}
        employeeName={`${employeeData?.firstName} ${employeeData?.lastName}`}
        profileImage={employeeData?.profileImage}
        setActive={() => setUpdate("")}
        probationPeriod={employeeData?.probationPeriod}
        handleSubmit={handleSubmit}
        loading={loading}
      />

      <PolicyUpdate
        active={update === EmployeeUpdateModal.POLICY}
        employeeData={employeeData}
        policy={policy}
        setActive={() => setUpdate("")}
        handleSubmit={handleSubmit}
        loading={loading}
      />
      <SalaryUpdate
        active={update === EmployeeUpdateModal.SALARY}
        employeeData={employeeData}
        employeeDetails={employeeDetails}
        payslip={payslip}
        setActive={() => setUpdate("")}
        refreshData={refreshData}
        loading={loading}
      />
      <BranchAssignmentUpdate
        active={
          update === EmployeeUpdateModal.REPORTING_BRANCH ||
          update === EmployeeUpdateModal.MANAGE_BRANCH
        }
        role={update}
        employeeData={employeeData}
        assignments={assignments}
        setActive={() => setUpdate("")}
        handleSubmit={handleSubmit}
        loading={loading}
      />
      <HistoryModal
        isOpen={historyOpen}
        handleOpenClose={handleHistoryOpenClose}
        history={history}
      />
      <AssignmentHistoryModal
        isOpen={assignmentHistoryOpen}
        handleOpenClose={handleAssignmentHistoryOpenClose}
        history={assignmentHistory}
      />
      <SalaryHistoryModal
        isOpen={salaryHistoryOpen}
        handleOpenClose={handleSalaryHistoryOpenClose}
        history={salaryHistory}
      />
    </>
  );
};

export default EmployeeDetailCard;
